import { Upload } from 'antd';
import { RcFile, UploadFile } from 'antd/lib/upload/interface';
import Notification from 'components/ui/base/notification/Notification';
import { createDocumentAes, createDocumentAis, createDocumentNcts, createDocumentUk } from './client';

export type DeclarationDocumentAttachFlags = {
    isAis?: boolean;
    isAes?: boolean;
    isNcts?: boolean;
    isCds?: boolean;
};

export type DeclarationDocumentAttachPayload = {
    fileId: string;
    fileReference: string;
    fileName: string;
    fileInfo?: { documentType?: string };
    invoice?: boolean;
    processInvoiceWithTee?: boolean; // A flag telling if we should trigger the invoice extraction (defaults to true)
};

export const attachDocumentToDeclaration = (
    document: DeclarationDocumentAttachPayload,
    declarationId: string,
    { isAis, isAes, isNcts, isCds }: DeclarationDocumentAttachFlags
): Promise<any> => {
    if (isAis) return createDocumentAis(document, declarationId);
    if (isAes) return createDocumentAes(document, declarationId);
    if (isNcts) return createDocumentNcts(document, declarationId);
    if (isCds) return createDocumentUk(document, declarationId);
    return Promise.resolve(undefined);
};

export const validateDeclarationFile = async (
    file: RcFile,
    customerId: string | undefined,
    declarationId: string | undefined,
    preUpload: Function,
    isAes: boolean,
    showGlobalOverlay?: Function
): Promise<boolean | string> => {
    if (!customerId || !declarationId) {
        console.error('Missing customer id or declaration id');
        return false;
    }

    const defaultFileTypes = { PDF: 'application/pdf', PNG: 'image/png', JPEG: 'image/jpeg' };
    let allowedFileTypes: Record<string, string> = { ...defaultFileTypes, TXT: 'text/plain', JPG: 'image/jpg' };

    if (isAes) {
        allowedFileTypes = {
            ...defaultFileTypes,
            GIF: 'image/gif',
            ZIP: 'application/x-zip-compressed',
            DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        };

        if (file.size > 10000000) {
            Notification({
                type: 'error',
                messageTitle: 'Maximum file size exceeded',
                description: `File ${file.name} couldn't be uploaded because it surpasses the file size limit: 10MB.`,
            });
            return Upload.LIST_IGNORE;
        }
    }

    const { dontComply } = filesComplyWithTypes([file], Object.values(allowedFileTypes));

    if (dontComply.length) {
        Notification({
            type: 'error',
            messageTitle: 'File type not supported',
            description: `File ${dontComply.map(({ name }) => name).join(', ')} couldn't be uploaded because it do not
                    comply with the allowed field types: ${Object.keys(allowedFileTypes).join(', ')}.`,
        });

        return Upload.LIST_IGNORE;
    }

    showGlobalOverlay?.({
        type: 'LoadingOverlay',
        payload: {
            message: 'File is uploading.',
        },
    });

    await preUpload({
        fileSize: file.size,
        customerId,
        declarationId,
        fileName: file.name,
    });
    return false;
};

const filesComplyWithTypes = (files: UploadFile<any>[] | RcFile[], types: string[]) => {
    const comply = [];
    const dontComply = [];

    for (const file of files) {
        if (!file.type) {
            dontComply.push(file);
        } else if (types.includes(file.type)) {
            comply.push(file);
        } else {
            dontComply.push(file);
        }
    }

    return { comply, dontComply };
};
